import {useCallback, useEffect, useMemo, useReducer} from 'react';

interface QuantityState {
  loading: boolean;
  showPicker: boolean;
}

type QuantityAction = {type: 'setLoading'; value: boolean} | {type: 'setShowPicker'; value: boolean};

function quantityReducer(state: QuantityState, action: QuantityAction): QuantityState {
  switch (action.type) {
    case 'setLoading':
      return {...state, loading: action.value};
    case 'setShowPicker':
      return {...state, showPicker: action.value};
    default:
      return state;
  }
}

const QuantityPickerManager = (() => {
  let openPickerId: string | null = null;
  const listeners = new Map<string, (isOpen: boolean) => void>();

  return {
    register: (id: string, callback: (isOpen: boolean) => void): (() => void) => {
      listeners.set(id, callback);
      return () => {
        listeners.delete(id);
      };
    },
    toggle: (id: string) => {
      const newOpenId = openPickerId === id ? null : id;
      if (newOpenId !== openPickerId) {
        if (openPickerId && listeners.has(openPickerId)) {
          listeners.get(openPickerId)?.(false);
        }
        if (newOpenId && listeners.has(newOpenId)) {
          listeners.get(newOpenId)?.(true);
        }
        openPickerId = newOpenId;
      }
    },
    getOpenPickerId: () => openPickerId
  };
})();

export function useFloatingQuantityPicker(product?: {id: string}) {
  const [state, dispatch] = useReducer(quantityReducer, {
    loading: false,
    showPicker: false
  });

  const closePicker = useCallback(() => {
    if (product?.id && QuantityPickerManager.getOpenPickerId() === product.id) {
      QuantityPickerManager.toggle(product.id);
    }
  }, [product?.id]);

  useEffect(() => {
    if (!state.showPicker) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target.closest('.quantity-picker-container')) {
        closePicker();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.showPicker, product?.id, closePicker]);

  useEffect(() => {
    if (!product?.id) return;

    const cleanup = QuantityPickerManager.register(product.id, (isOpen) => {
      dispatch({type: 'setShowPicker', value: isOpen});
    });

    return () => {
      cleanup?.();
    };
  }, [product?.id]);

  const togglePicker = useCallback(() => {
    if (product?.id && product) {
      QuantityPickerManager.toggle(product.id);
    }
  }, [product]);

  const setLoading = useCallback((loading: boolean) => {
    dispatch({type: 'setLoading', value: loading});
  }, []);

  return useMemo(
    () => ({
      loading: state.loading,
      showPicker: state.showPicker,
      togglePicker,
      closePicker,
      setLoading
    }),
    [state.loading, state.showPicker, togglePicker, closePicker, setLoading]
  );
}
